import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ToastrService} from 'ngx-toastr';

import {filter} from 'rxjs';
import {IDropdownObject} from 'src/app/interfaces/dropdown-object.interface';
import {IPersonalRecruiterEditMode} from 'src/app/interfaces/employee-edit-mode.interface';
import {Tab} from 'src/app/interfaces/employee.interface';
import {IMessengers} from 'src/app/interfaces/messengers.interface';
import {
  EmailFormGroup,
  IDirtyPhoneNumbers,
  IPersonalRecruiterData,
  PersonalRecruiterFormGroup,
} from 'src/app/interfaces/personal-data.interface';
import {CloseHandlerService} from 'src/app/services/close-handler.service';
import {EmployeeService} from 'src/app/services/employee.service';
import {Utils} from 'src/app/shared/utils/utils';

@UntilDestroy()
@Component({
  selector: 'app-personal-recruiter',
  templateUrl: './personal-recruiter.component.html',
  styleUrls: ['./personal-recruiter.component.scss'],
})
export class PersonalRecruiterComponent implements OnInit {
  public employeeId!: number;
  public personalForm!: FormGroup;
  public formValueDefault!: IPersonalRecruiterData;
  public countryList: IDropdownObject[] = [];
  public stackTechnologyList: IDropdownObject[] = [];
  public phoneNumbersComponentData: IMessengers = {
    phoneNumber: '',
    isTelegram: false,
    isViber: false,
    isWhatsApp: false,
  };
  public firstLoad = true;
  public isEditable = false;
  public isEqualObjects = Utils.isEqualObjects;
  public isFieldInvalid = Utils.isFieldInvalid;
  public validateAllFormFields = Utils.validateAllFormFields;

  @ViewChild('saveButton') saveButton!: ElementRef;

  constructor(
    private employeeService: EmployeeService,
    private route: ActivatedRoute,
    private closeHandler: CloseHandlerService,
    private notifier: ToastrService
  ) {}

  ngOnInit(): void {
    this.employeeId = +this.route.parent?.snapshot.params['id'];
    this.personalForm = this.getPersonalForm();

    this.getTabData();

    this.subscribeToFormValueChanges();

    this.configureCloseHandler();
  }

  getPersonalForm(): FormGroup<PersonalRecruiterFormGroup> {
    return new FormGroup<PersonalRecruiterFormGroup>({
      id: new FormControl<number | null>(null),
      country: new FormControl<IDropdownObject | null>(
        null,
        Validators.required
      ),
      city: new FormControl<string | null>(null, [
        Validators.required,
        Validators.maxLength(250),
      ]),
      email: new FormGroup<EmailFormGroup>({
        id: new FormControl<number | null>(null),
        employeeId: new FormControl<number | null>(null),
        email: new FormControl<string | null>(null),
      }),
      phoneNumber: new FormControl<IMessengers | null>(null),
      stackTechnology: new FormControl<IDropdownObject | null>(null),
    });
  }

  getTabData() {
    this.employeeService
      .getTabData<IPersonalRecruiterData>(
        Tab.PersonalRecruiter,
        this.employeeId
      )
      .pipe(untilDestroyed(this))
      .subscribe(data => {
        this.personalForm.patchValue(data);
        this.formValueDefault = data;
        if (data.country?.name) {
          this.countryList = [data.country];
        }
        if (data.stackTechnology) {
          this.stackTechnologyList = [data.stackTechnology];
        }

        if (data.phoneNumber) {
          this.phoneNumbersComponentData = data.phoneNumber;
        }
      });
  }

  subscribeToFormValueChanges() {
    this.personalForm.valueChanges
      .pipe(
        untilDestroyed(this),
        filter(
          (value: IPersonalRecruiterData) =>
            value &&
            this.isEditable &&
            this.personalForm.dirty &&
            !this.isEqualObjects(value, this.formValueDefault)
        )
      )
      .subscribe(() => {
        this.closeHandler.notSaved = true;
      });
  }

  configureCloseHandler() {
    this.closeHandler.notSaveForm.pipe(untilDestroyed(this)).subscribe(() => {
      this.personalForm.markAsUntouched();
      this.isEditable = false;
      this.personalForm.patchValue(this.formValueDefault);
    });

    this.closeHandler.clickSavePopup
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.saveButton.nativeElement.click();
      });
  }

  switchEdit(event: Event) {
    event.preventDefault();
    this.isEditable = !this.isEditable;
    if (this.isEditable && this.firstLoad) {
      this.employeeService
        .getDropdownItems<IPersonalRecruiterEditMode>(Tab.PersonalRecruiter)
        .pipe(untilDestroyed(this))
        .subscribe((data: IPersonalRecruiterEditMode) => {
          this.countryList = data.Country;
          this.stackTechnologyList = data.StackTechnologies;
          this.firstLoad = false;
        });
    }
  }

  setPhoneNumber(event: IDirtyPhoneNumbers) {
    this.personalForm.controls['phoneNumber'].setValue(event.value);
  }

  save() {
    if (this.personalForm.invalid) {
      this.validateAllFormFields(this.personalForm);
      return;
    }

    this.formValueDefault = this.personalForm.getRawValue();
    this.closeHandler.notSaved = false;

    this.employeeService
      .postTabData(Tab.PersonalRecruiter, this.personalForm.getRawValue())
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.isEditable = !this.isEditable;
        this.notifier.success('Saved');
      });
  }

  get country(): AbstractControl {
    return this.personalForm.get('country')!;
  }

  get city(): AbstractControl {
    return this.personalForm.get('city')!;
  }

  get email(): AbstractControl {
    return this.personalForm.get('email')!;
  }

  get stackTechnology(): AbstractControl {
    return this.personalForm.get('stackTechnology')!;
  }
}
